import { useState, useCallback, useContext } from 'react';
import FirebaseContext from '../../context/FirebaseContext';
import LoggedInUserContext from '../../context/LoggedInUserContext';
import UserContext from '../../context/UserContext';
import MealPlanCreationRequest from '../Model/MealPlanCreationRequest';
import useToast from './useToast';

/**
 * Custom hook to manage AI meal plan generation flow
 * @param {Object} options - Configuration options
 * @param {boolean} options.isTemplate - Whether this is for a template (true) or user assignment (false)
 * @param {string} options.userId - User ID for user assignment flow
 * @param {Object} options.mealBreakdown - Meal breakdown object for template flow
 * @param {Function} options.setMealTimes - State setter for meal times
 * @param {Function} options.setAiGeneratedRecipes - State setter for AI generated recipe IDs (for user assignment flow)
 * @param {Function} options.onIsAIGeneratedChange - Callback to notify AI generation status (for user assignment flow)
 * @param {string} options.errorMessage - Error message to show on failure
 * @param {string} options.successMessage - Success message to show on completion
 * @returns {Object} - Hook return value with state and handlers
 */
const useAIMealPlanGeneration = ({
  isTemplate = false,
  userId = null,
  mealBreakdown = null,
  setMealTimes = null,
  setAiGeneratedRecipes = null,
  onIsAIGeneratedChange = null,
  errorMessage = 'Error generating AI meal plan. Please try again later.',
  successMessage = null,
} = {}) => {
  const { firebase: { remote } } = useContext(FirebaseContext);
  const { userId: loggedInUserId } = useContext(LoggedInUserContext);
  const { userId: coachUserId } = useContext(UserContext);
  const { showToast } = useToast();

  const [aiMealPlannerRequestInFlight, setAiMealPlannerRequestInFlight] = useState(false);
  const [aiMealPlanModalOpen, setAIMealPlanModalOpen] = useState(false);
  const [aiMealPlannerRequestId, setAiMealPlannerRequestId] = useState(null);

  const handleAIMealPlanGeneration = useCallback(async (userOverrides, additionalQuery) => {
    try {
      setAiMealPlannerRequestInFlight(true);

      const requestParams = {
        userId: isTemplate ? loggedInUserId : userId,
        userOverrides,
        ...(coachUserId && { coachId: coachUserId }),
        ...(additionalQuery && { additionalQuery: { query: additionalQuery } }),
      };

      if (isTemplate) {
        requestParams.isTemplate = true;
        requestParams.mealBreakdown = mealBreakdown;
      }

      const response = await remote(
        'aiMealPlanner-request-generate',
        requestParams,
        { method: 'POST' },
      );
      const responseToJson = await response.json();
      setAiMealPlannerRequestId(responseToJson.requestId);
    } catch (error) {
      showToast(errorMessage, { error: true });
      setAiMealPlannerRequestInFlight(false);
    }
  }, [
    remote,
    isTemplate,
    userId,
    loggedInUserId,
    coachUserId,
    mealBreakdown,
    errorMessage,
    showToast,
  ]);

  const handleCloseAIMealPlanModal = useCallback(async (userOverrides, additionalQuery) => {
    setAIMealPlanModalOpen(false);
    await handleAIMealPlanGeneration(userOverrides, additionalQuery);
  }, [handleAIMealPlanGeneration]);

  const openAIMealPlanModal = useCallback(() => {
    setAIMealPlanModalOpen(true);
  }, []);

  const handleGenerationComplete = useCallback(async (requestId) => {
    try {
      const mealPlanCreationRequestDoc = await MealPlanCreationRequest.getMealPlanCreationRequest(requestId);

      if (!mealPlanCreationRequestDoc) {
        showToast(errorMessage, { error: true });
        setAiMealPlannerRequestId(null);
        setAiMealPlannerRequestInFlight(false);
        return;
      }

      // Handle template flow - add recipes to existing meal times
      if (isTemplate && setMealTimes) {
        const imagesByRecipeId = mealPlanCreationRequestDoc.images?.reduce((acc, image) => {
          acc.set(image.recipeId, image);
          return acc;
        }, new Map()) || new Map();

        // Group recipes by meal type
        const recipesByMealType = mealPlanCreationRequestDoc.recipes?.reduce((acc, recipe) => {
          const mealType = recipe.mealType.toUpperCase();
          if (!acc.has(mealType)) {
            acc.set(mealType, []);
          }
          acc.get(mealType).push({
            ...recipe,
            image: imagesByRecipeId.get(recipe.id)?.imageUrl,
          });
          return acc;
        }, new Map()) || new Map();

        // Add recipes to existing meal times buckets
        setMealTimes((prevMealTimes) => {
          const updatedMealTimes = prevMealTimes.map((mealTime) => {
            const mealTypeName = mealTime.name.toUpperCase();
            const recipesForMealType = recipesByMealType.get(mealTypeName) || [];

            if (recipesForMealType.length === 0) {
              return mealTime;
            }

            // Add recipes to the meal time's meals array
            const newMeals = recipesForMealType.map((recipe) => ({
              recipe,
              servings: 1,
            }));

            return {
              ...mealTime,
              meals: [...(mealTime.meals || []), ...newMeals],
            };
          });
          return updatedMealTimes;
        });

        if (successMessage) {
          showToast(successMessage);
        }
      } else if (!isTemplate && setMealTimes) {
        // Handle user assignment flow - create meal times from scratch
        const recipeIds = mealPlanCreationRequestDoc.recipes?.map((recipe) => recipe.id) || [];

        const mealTimesMap = mealPlanCreationRequestDoc.recipes?.reduce((acc, recipe) => {
          const mealType = recipe.mealType.toUpperCase();
          if (!acc.has(mealType)) {
            acc.set(mealType, []);
          }
          acc.get(mealType).push(recipe);
          return acc;
        }, new Map()) || new Map();

        const mealTimes = [];

        const imagesByRecipeId = mealPlanCreationRequestDoc.images?.reduce((acc, image) => {
          acc.set(image.recipeId, image);
          return acc;
        }, new Map()) || new Map();

        // Fallback in case the meal breakdown is unavailable
        const getCaloricSplit = (mealType) => {
          if (mealType === 'BREAKFAST') {
            return 30;
          }
          if (mealType === 'LUNCH') {
            return 30;
          }
          if (mealType === 'DINNER') {
            return 30;
          }
          if (mealType === 'SNACK') {
            return 10;
          }
          return 0;
        };

        mealTimesMap.forEach((recipes, mealType) => {
          mealTimes.push({
            caloricSplit: mealPlanCreationRequestDoc.mealBreakdown?.[mealType] || getCaloricSplit(mealType),
            name: mealType,
            meals: recipes.map((recipe) => ({
              recipe: { ...recipe, image: imagesByRecipeId.get(recipe.id)?.imageUrl },
              servings: 1,
            })),
          });
        });

        setMealTimes(mealTimes);
        if (setAiGeneratedRecipes) {
          setAiGeneratedRecipes(recipeIds);
        }
        if (onIsAIGeneratedChange) {
          onIsAIGeneratedChange(true);
        }
      }
    } catch (error) {
      showToast(errorMessage, { error: true });
    } finally {
      setAiMealPlannerRequestId(null);
      setAiMealPlannerRequestInFlight(false);
    }
  }, [
    isTemplate,
    setMealTimes,
    setAiGeneratedRecipes,
    onIsAIGeneratedChange,
    errorMessage,
    successMessage,
    showToast,
  ]);

  return {
    aiMealPlannerRequestInFlight,
    aiMealPlanModalOpen,
    aiMealPlannerRequestId,
    handleAIMealPlanGeneration,
    handleCloseAIMealPlanModal,
    openAIMealPlanModal,
    handleGenerationComplete,
    setAIMealPlanModalOpen,
    setAiMealPlannerRequestId,
  };
};

export default useAIMealPlanGeneration;
